import { type MouseEvent, useMemo } from 'react';

import {
  Box,
  Button,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { formatDistance } from 'date-fns';
import { House } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardListingsQuery } from '@graphql/__generated__/graphql';
import { getLotStatusLabel } from '@modules/_shared/components/LotCardInfo';
import { Tag } from '@modules/_shared/components/Tag';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import { getListingPrices } from '@modules/_shared/utils/propertyTemplateFunctions';
import { useLocale } from '@shared/hooks/locale';

type DashboardListingsCardItemProps = {
  listing: GetDashboardListingsQuery['lots'][number];
};

const outlineChipSx = (color: string, borderColor: string): SxProps<Theme> => ({
  border: 1,
  borderColor,
  color,
  px: 1,
  py: 0.125,
  borderRadius: 1,
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1.5,
});

type ListingStatus = GetDashboardListingsQuery['lots'][number]['status'];

const getStatusColors = (
  status: ListingStatus,
): { color: string; borderColor: string } => {
  switch (status) {
    case 'available':
      return { color: 'primary.main', borderColor: 'primary.main' };
    case 'available_coming_soon':
    case 'reserved':
      return { color: 'info.main', borderColor: 'info.main' };
    case 'available_off_market':
    case 'paused':
      return { color: 'warning.main', borderColor: 'warning.main' };
    default:
      return { color: 'text.secondary', borderColor: 'grey.300' };
  }
};

export const DashboardListingsCardItem = ({
  listing,
}: DashboardListingsCardItemProps) => {
  const { t, locale, dateLocale } = useLocale();
  const navigate = useNavigate();

  const statusColors = getStatusColors(listing.status);
  const statusLabel =
    getLotStatusLabel(listing.status ?? '', t) ?? listing.status ?? '';

  const propertyAddress = listing.property
    ? formatAddress(listing.property, ', ')
    : listing.title || t('No address');

  const imageUrl = listing.property?.property_images?.[0]?.image?.url ?? null;

  const creationTimeAgo = useMemo(
    () =>
      formatDistance(new Date(listing.created_at), new Date(), {
        locale: dateLocale,
        addSuffix: true,
      }),
    [listing.created_at, dateLocale],
  );

  const listingPath = `/dashboard/listings/${listing.id}`;
  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    navigateWithModifiers(event, navigate, listingPath);
  };
  const handleButtonClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleOpenClick(e);
  };

  const livePortalsCount =
    listing.portal_listings_aggregate.aggregate?.count ?? 0;
  const matchesCount = listing.buyer_leads_aggregate.aggregate?.count ?? 0;
  const enquiriesCount = listing.enquiries_aggregate.aggregate?.count ?? 0;

  const { primary: priceLabel } = getListingPrices(listing, t, locale);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleOpenClick}
      onKeyDown={makeRowKeyDownHandler(listingPath, navigate)}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'stretch',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'grey.300',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: 128 },
          height: { xs: 120, sm: 'auto' },
          alignSelf: 'stretch',
          flexShrink: 0,
          overflow: 'hidden',
          bgcolor: 'grey.200',
        }}
      >
        {imageUrl ? (
          <Image
            src={imageUrl}
            css={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            options={{ w: 512, h: 384, f: 'jpg' }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'grey.400',
            }}
          >
            <House size={32} strokeWidth={1.5} />
          </Box>
        )}
        {livePortalsCount > 0 && (
          <Box sx={{ position: 'absolute', top: 6, left: 6 }}>
            <Tag status="dark_success" noBorder>
              {t('Online')}
              <Box component="span" sx={{ ml: 0.75, opacity: 0.8 }}>
                {livePortalsCount}
              </Box>
            </Tag>
          </Box>
        )}
      </Box>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          gap: 0.25,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            {propertyAddress}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {creationTimeAgo}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 700,
          }}
        >
          {priceLabel ?? t('Price on request')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 0.25,
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: 0.75,
              alignItems: 'center',
              minWidth: 0,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={outlineChipSx(statusColors.color, statusColors.borderColor)}
            >
              {statusLabel}
            </Box>
            <Box sx={outlineChipSx('text.secondary', 'grey.300')}>
              {t('{{count}} matches', { count: matchesCount })}
            </Box>
            <Box sx={outlineChipSx('text.secondary', 'grey.300')}>
              {t('{{count}} enquiries', { count: enquiriesCount })}
            </Box>
          </Stack>
          <Button
            size="small"
            variant="soft"
            color="primary"
            sx={{ flexShrink: 0 }}
            onClick={handleButtonClick}
            onAuxClick={handleButtonClick}
          >
            {t('Open')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
